import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = { 
    url: String, 
    cursor: String, 
    loading: { type: Boolean, default: false },
    threshold: { type: Number, default: 500 } // pixels from bottom
  }
  
  connect() {
    // Bind the scroll handler
    this.boundCheckScroll = this.checkScroll.bind(this)
    
    // Listen to window scroll since we don't have a fixed height container
    window.addEventListener('scroll', this.boundCheckScroll)
    window.addEventListener('resize', this.boundCheckScroll)
    
    // Initial check after DOM is ready
    setTimeout(() => this.checkScroll(), 100)
  }
  
  disconnect() {
    window.removeEventListener('scroll', this.boundCheckScroll)
    window.removeEventListener('resize', this.boundCheckScroll)
  }
  
  checkScroll() {
    if (this.loadingValue) {
      return
    }
    
    if (!this.cursorValue || this.cursorValue === '') {
      console.log('No cursor value, showing end of results')
      this.showEndOfResults()
      return
    }
    
    // Use window scroll since we removed the height limitation
    const windowHeight = window.innerHeight
    const documentHeight = document.documentElement.scrollHeight
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop
    const scrollPosition = scrollTop + windowHeight
    const distanceFromBottom = documentHeight - scrollPosition
    
    console.log('Scroll check:', {
      distanceFromBottom,
      threshold: this.thresholdValue,
      shouldLoad: distanceFromBottom <= this.thresholdValue,
      cursor: this.cursorValue
    })
    
    // Check if we should load more
    if (distanceFromBottom <= this.thresholdValue) {
      this.loadMore()
    }
  }
  
  async loadMore() {
    console.log('Loading more records with cursor:', this.cursorValue)
    this.loadingValue = true
    this.showLoadingIndicator()
    
    try {
      const url = new URL(this.urlValue || window.location.href, window.location.origin)
      
      // Preserve existing search params (like search term)
      const currentParams = new URLSearchParams(window.location.search)
      currentParams.forEach((value, key) => {
        if (key !== 'cursor') {
          url.searchParams.set(key, value)
        }
      })
      
      url.searchParams.set('cursor', this.cursorValue)
      console.log('Fetching URL:', url.toString())
      
      // Get current row count - find tbody dynamically
      const tbody = this.element.querySelector('tbody[id$="_tbody"]')
      const currentRowCount = tbody ? tbody.querySelectorAll('tr').length : 0
      url.searchParams.set('current_count', currentRowCount)
      
      const response = await fetch(url, {
        headers: {
          'Accept': 'text/vnd.turbo-stream.html',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      
      if (response.ok) {
        const html = await response.text()
        console.log('Received response, processing turbo stream...')
        // Turbo will handle the stream response
        Turbo.renderStreamMessage(html)
        
        // Update cursor from DOM after Turbo processes
        setTimeout(() => {
          console.log('Updating cursor from DOM...')
          this.updateCursorFromDOM()
        }, 100)
      } else {
        console.error('Failed response:', response.status, response.statusText)
      }
    } catch (error) {
      console.error('Failed to load more records:', error)
      this.showError()
    } finally {
      this.loadingValue = false
      this.hideLoadingIndicator()
    }
  }
  
  updateCursorFromDOM() {
    const cursorElement = document.getElementById('cursor_state')
    console.log('Looking for cursor_state element:', cursorElement)
    
    if (cursorElement) {
      const newCursor = cursorElement.dataset.nextCursor || ''
      const hasMore = cursorElement.dataset.hasMore === 'true'
      
      console.log('Found cursor state:', { newCursor, hasMore })
      
      this.cursorValue = newCursor
      
      if (!hasMore || !newCursor) {
        console.log('No more records to load')
        this.showEndOfResults()
      } else {
        console.log('Updated cursor to:', newCursor)
      }
    } else {
      console.error('cursor_state element not found!')
    }
  }
  
  showLoadingIndicator() {
    const indicator = this.element.querySelector('[data-infinite-scroll-loading]')
    if (indicator) indicator.classList.remove('hidden')
  }
  
  hideLoadingIndicator() {
    const indicator = this.element.querySelector('[data-infinite-scroll-loading]')
    if (indicator) indicator.classList.add('hidden')
  }
  
  showError() {
    const errorElement = this.element.querySelector('[data-infinite-scroll-error]')
    if (errorElement) {
      errorElement.classList.remove('hidden')
      setTimeout(() => errorElement.classList.add('hidden'), 5000)
    }
  }
  
  showEndOfResults() {
    const endElement = this.element.querySelector('[data-infinite-scroll-end]')
    if (endElement) endElement.classList.remove('hidden')
  }
}